import React, { useEffect } from "react";
import "../src/i18n/i18n";
import "./App.css";
import AppWidget from "./AppWidget";
import { SettingsProvider, useSettings } from "./context/SettingsContext";
import { removeAllFeatures } from "./utils/cursor.helper";
import {
  postMouseMoveMessage,
} from "./utils/message.helper";

// This hook manages adding/removing a class on body based on oversizeWidget
const useOversizeWidgetClass = (oversizeWidget: boolean) => {
  useEffect(() => {
    if (oversizeWidget) {
      document.body.classList.add("oversize");
    } else {
      document.body.classList.remove("oversize");
    }
  }, [oversizeWidget]);
};

const AppContent: React.FC = () => {
  const { oversizeWidget } = useSettings();

  // Inside the iframe's JavaScript
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data.action === "applyBigCursor") {
        const style = document.createElement("style");
        style.id = "iframeCursorStyle";
        style.textContent = `* { cursor: url(${e.data.cursorUrl}), auto !important; }`;
        document.head.appendChild(style);
      }
      if (e.data.action === "removeBigCursor") {
        const style = document.getElementById("iframeCursorStyle");
        if (style) style.remove();
      }
      if (e.data.action === "applyBigDarkCursor") {
        const style = document.createElement("style");
        style.id = "iframeCursorStyle";
        style.textContent = `* { cursor: url(${e.data.cursorUrl}), auto !important; }`;
        document.head.appendChild(style);
      }
      if (e.data.action === "removeBigDarkCursor") {
        const style = document.getElementById("iframeCursorStyle");
        if (style) style.remove();
        const makesureremovedarkcursorlink = document.getElementById(
          "applyBigDarkReadingCursor"
        );
        if (makesureremovedarkcursorlink) makesureremovedarkcursorlink.remove();
        return;
      }

      if (e.data.action === "applyBigReadingCursor") {
        const existingStyle = document.getElementById("bigCursorReadingStyle");
        if (existingStyle) {
          existingStyle.remove();
        }
        const style = document.createElement("style");
        style.id = "applyBigReadingCursor";
        // Add a fallback (auto) so browsers can revert if needed
        style.textContent = `* { cursor: url(${e.data.cursorUrl}), auto !important; }`;
        document.head.appendChild(style);
      }
      if (e.data.action === "removeBigReadingCursor") {
        const style = document.getElementById("bigCursorReadingStyle");
        if (style) style.remove();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Iframe mouse listener logic
  useEffect(() => {
    let iframeMouseListenerActive = false;

    const handleMouseMoveInIframe = (mouseEvent: MouseEvent) => {
      postMouseMoveMessage(mouseEvent.clientX, mouseEvent.clientY);
    };

    const handleParentMessage = (event: MessageEvent) => {
      if (!event.data || !event.data.action) return;

      const { action } = event.data;

      if (
        action === "parentReadingMaskActive" ||
        action === "parentReadingGuideActive"
      ) {
        if (!iframeMouseListenerActive) {
          document.addEventListener("mousemove", handleMouseMoveInIframe);
          iframeMouseListenerActive = true;
        }
      } else if (
        action === "parentReadingMaskInactive" ||
        action === "parentReadingGuideInactive" ||
        action === "unSelectedAppCursors"
      ) {
        if (iframeMouseListenerActive) {
          document.removeEventListener("mousemove", handleMouseMoveInIframe);
          iframeMouseListenerActive = false;
        }
      }

      if (action === "unSelectedAppCursors") {
        removeAllFeatures();
      }
    };

    window.addEventListener("message", handleParentMessage);

    return () => {
      window.removeEventListener("message", handleParentMessage);
      if (iframeMouseListenerActive) {
        document.removeEventListener("mousemove", handleMouseMoveInIframe);
      }
    };
  }, []);

  // Listen for forwarded keyboard events from the parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "KEYBOARD" && event.data?.action === "KEYDOWN") {
        // This is a forwarded keyboard event from widget-script.
        // We dispatch it locally so all useKeyboardShortcuts hooks can react to it.
        const keyboardEventData = event.data.data;
        window.dispatchEvent(new KeyboardEvent("keydown", keyboardEventData));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    const handleTab = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        // Updated focusable elements selector
        const focusableElements = document.querySelectorAll(
          'button:not([disabled]), [href]:not([tabindex="-1"]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])'
        );

        // Filter visible elements
        const visibleFocusableElements = Array.from(focusableElements).filter(
          (el) => {
            const element = el as HTMLElement;
            const style = window.getComputedStyle(element);

            return (
              element.offsetWidth > 0 &&
              element.offsetHeight > 0 &&
              style.visibility !== "hidden" &&
              style.display !== "none" &&
              (element === document.activeElement ||
                Number(element.getAttribute("tabindex")) !== -1)
            );
          }
        ) as HTMLElement[];

        if (visibleFocusableElements.length === 0) return;

        const firstElement = visibleFocusableElements[0];
        const lastElement =
          visibleFocusableElements[visibleFocusableElements.length - 1];

        const applyFocusStyles = (element: HTMLElement) => {
          // Clear previous styles from all elements
          visibleFocusableElements.forEach((el) => {
            el.style.outline = "";
            el.style.outlineOffset = "";
            el.style.borderRadius = "";
          });

          // Apply new styles
          element.style.outline = "2px solid black";
          element.style.outlineOffset = "-2px";
          element.style.borderRadius = "20px";

          // Focus logic
          requestAnimationFrame(() => {
            element.focus({ preventScroll: true });
            element.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          });
        };

        // Handle shift+tab
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            applyFocusStyles(lastElement);
            event.preventDefault();
          } else {
            const currentIndex = visibleFocusableElements.findIndex(
              (el) => el === document.activeElement
            );
            const targetIndex =
              currentIndex > 0
                ? currentIndex - 1
                : visibleFocusableElements.length - 1;
            applyFocusStyles(visibleFocusableElements[targetIndex]);
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            applyFocusStyles(firstElement);
            event.preventDefault();
          } else {
            const currentIndex = visibleFocusableElements.findIndex(
              (el) => el === document.activeElement
            );
            const targetIndex =
              currentIndex < visibleFocusableElements.length - 1
                ? currentIndex + 1
                : 0;
            applyFocusStyles(visibleFocusableElements[targetIndex]);
            event.preventDefault();
          }
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, []);

  useOversizeWidgetClass(oversizeWidget);

  return (
    <div className="App bg-transparent" id="App">
      <div id="google_translate_element" className="hidden"></div>
      <div className="relative min-h-[50vh] sm:min-h-screen sm:px-0">
        <AppWidget />
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const lang = "de"; // or dynamically set

  return (
    <SettingsProvider lang={lang}>
      <AppContent />
    </SettingsProvider>
  );
};

export default App;
